import { h, Fragment } from "preact";
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import ProductOptionPill from "./product-option-pill";
import ProductOptionSwatchHeader from "./product-option-swatch-header";

// Constant to estimate pill size
const GAP = 8;
const MIN_COLUMNS = 2;
const MAX_COLUMNS = 7;
const CHAR_WIDTH = 7;
const PILL_CHROME = 22;

// Char-count fallback for the first paint, before the container is measured.
function estimateColumns(maxValueLength, valueCount) {
  const maxColumns =
    maxValueLength > 15
      ? 2
      : maxValueLength > 10
        ? 3
        : maxValueLength > 6
          ? 4
          : MAX_COLUMNS;
  return Math.min(maxColumns, valueCount);
}

export default function ProductOptionPills({
  optionValues,
  optionName,
  optionLabel,
  updateOptionValue,
  selectedOptionValue,
  disabledOptionValues = [],
  sizeGuide,
}) {
  const singleValue = optionValues.length === 1;
  const listRef = useRef(null);

  const maxValueLength = Math.max(
    ...optionValues.map((optionValue) => optionValue.name.length),
  );
  const [pillColumns, setPillColumns] = useState(() =>
    estimateColumns(maxValueLength, optionValues.length),
  );

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const calc = () => {
      const containerWidth = list.clientWidth;
      if (!containerWidth) return;

      // Widest pill the grid must fit without wrapping.
      const requiredPillWidth = maxValueLength * CHAR_WIDTH + PILL_CHROME;

      // Largest N where (W - (N-1)*GAP) / N >= requiredPillWidth, i.e.
      // N <= (W + GAP) / (requiredPillWidth + GAP).
      const maxFit = Math.floor(
        (containerWidth + GAP) / (requiredPillWidth + GAP),
      );
      const columns = Math.max(
        MIN_COLUMNS,
        Math.min(MAX_COLUMNS, optionValues.length, maxFit),
      );
      setPillColumns(columns);
    };

    calc();
    const observer = new ResizeObserver(calc);
    observer.observe(list);
    return () => observer.disconnect();
  }, [optionValues]);

  return (
    <div className={`product-options__option product-options__option--pills`}>
      <ProductOptionSwatchHeader
        optionName={optionName}
        optionLabel={optionLabel}
        selectedOptionValue={singleValue ? selectedOptionValue : null}
        sizeGuide={sizeGuide}
      />
      {singleValue === false && (
        <div className="product-options__option-content" data-content>
          <div className="product-options__option-content-inner">
            <fieldset
              ref={listRef}
              className="product-options__pill-list"
              style={{ "--pill-columns": pillColumns }}
            >
              <legend class="visually-hidden">{optionLabel}</legend>
              {optionValues.map((optionValue) => (
                <ProductOptionPill
                  key={optionValue.name}
                  optionValue={optionValue}
                  optionName={optionName}
                  disabled={disabledOptionValues.includes(optionValue.name)}
                  updateOptionValue={updateOptionValue}
                  selected={optionValue.name === selectedOptionValue}
                />
              ))}
            </fieldset>
          </div>
        </div>
      )}
    </div>
  );
}
